// The x axis of every date-based chart: where its ticks go, what they say, and
// where a year turns. One copy, so a summary card's Trend, its Coverage Trend
// and the full-size feature chart label dates the same way.

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** A tick reads `Thu 10/1`; `withYear` gives `10/1/2026`, for tooltips. The
 *  axis never spends a label on the year — `yearBoundaries` marks it. */
export function formatDateLabel(ts: number, withYear: boolean): string {
  const d = new Date(ts)
  const m = d.getMonth() + 1
  const day = d.getDate()
  return withYear ? `${m}/${day}/${d.getFullYear()}` : `${WEEKDAY_SHORT[d.getDay()]} ${m}/${day}`
}

/** A month-cadence tick reads `Oct`; the year is the boundary line's job. */
export function formatMonthLabel(ts: number): string {
  return MONTH_SHORT[new Date(ts).getMonth()]
}

/**
 * About `count` x-axis ticks for a daily time axis, each on a local midnight.
 *
 * Evenly spaced ticks between the first and last point read well over a long
 * span, but over a few days they land mid-day: two fall inside one day and
 * share its label, and a tick at 9pm reads as that day while the point drawn
 * beside it is the next one's. Snapping each to the nearest midnight puts
 * every label under the point it names, and a span of fewer days than ticks
 * simply gets one tick a day.
 *
 * Inset 5% from the data edges so labels don't sit at x=0 / x=W; a snapped
 * tick that would leave the data's range is dropped.
 */
export function dayTicks(start: number, end: number, count = 5): number[] | undefined {
  if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined
  if (start >= end) return [start]
  const inset = 0.05
  const span = end - start
  const first = start + inset * span
  const last = end - inset * span
  const out: number[] = []
  for (let i = 0; i < count; i++) {
    const tick = nearestMidnight(first + ((last - first) * i) / (count - 1))
    if (tick >= start && tick <= end && out[out.length - 1] !== tick) out.push(tick)
  }
  return out
}

/** The local midnight closest to `ts` — by calendar, not by adding 24h, so a
 *  DST day's 23 or 25 hours land on the right date. */
function nearestMidnight(ts: number): number {
  const d = new Date(ts)
  const before = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const after = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime()
  return ts - before < after - ts ? before : after
}

/** Every Jan 1 strictly inside (start, end): where to draw a year line. */
export function yearBoundaries(start: number, end: number): number[] {
  if (!(end > start)) return []
  const out: number[] = []
  for (let y = new Date(start).getFullYear() + 1; y <= new Date(end).getFullYear(); y++) {
    const jan1 = new Date(y, 0, 1).getTime()
    if (jan1 > start && jan1 < end) out.push(jan1)
  }
  return out
}

/** A year line's `ReferenceLine` props: thin and gray, labelled with the year
 *  *before* the flip. Props rather than a component, because recharts only
 *  draws a `ReferenceLine` that is the chart's direct child. */
export function yearBoundaryLineProps(ts: number) {
  return {
    x: ts,
    stroke: '#9ca3af',
    strokeWidth: 1,
    ifOverflow: 'hidden' as const,
    label: {
      value: String(new Date(ts).getFullYear() - 1),
      position: 'insideTopLeft' as const,
      fill: '#6b7280',
      fontSize: 11,
    },
  }
}
